<template>
	<div class="dropdown__list-container">
		<Search
			v-if="
				(options.length > 20 ||
					searchQuery.length > 0 ||
					loadingSearch) &&
					showSearchOptions
			"
			v-model="searchQuery"
			:timeout="timeout"
		/>
		<ul v-if="!loadingSearch" class="dropdown-list" ref="dropdownlist">
			<li
				v-for="(item, index) in optionsLocal"
				:id="index"
				:class="[
					'dropdown-list__item',
					item.value === value ? 'is-active' : '',
					item.disabled ? 'is-disabled' : ''
				]"
				:key="index"
				:tabindex="0"
				@click="selectItem(item.value)"
				@keydown.enter="selectItem(item.value)"
				@keydown.up="previousItem"
				@keydown.down="nextItem"
			>
				<div class="list-item">
					<a class="list-item__link" tabindex="-1">
						{{ item.text }}
					</a>
				</div>
			</li>
			<Button
				v-if="moreToLoad && !loadingMore"
				labelText="Load More"
				className="btn--link"
				:btnClick="loadMoreHandle"
			/>
			<Spinner
				v-if="loadingMore"
				:processor="{ loading: true }"
				variant="infinity-scroll"
			/>
		</ul>
		<Spinner
			v-if="loadingSearch"
			:processor="{ loading: true }"
			variant="infinity-scroll"
		/>
	</div>
</template>

<script>
import Search from '@/components/forms/search/search';
import Button from '@/components/forms/button/button';
import Spinner from '@/components/visual/spinner/spinner';

export default {
	name: 'DropdownListV2',
	components: {
		Search,
		Button,
		Spinner
	},
	props: {
		value: [String, Number, Boolean],
		options: {
			type: Array,
			required: true
		},
		timeout: { type: Number, default: 300 },
		moreToLoad: Boolean, // set to true if there are more elements to load,
		showSearchOptions: {
			type: Boolean,
			default: true
		}
	},
	data() {
		return {
			optionsLocal: [],
			searchQuery: '',
			loadingMore: false,
			loadingSearch: false
		};
	},
	created() {
		this.optionsLocal = Array.from(this.options);
	},
	methods: {
		previousItem() {
			// to implement
		},
		nextItem() {
			// to implement
		},
		selectItem(value) {
			if (!this.loadingMore && !this.loadingSearch) {
				this.$emit('input', value);
				this.$emit('change', value);
			}
		},
		fixScrolling() {
			const liH = this.$refs.options[this.currentItem].clientHeight;
			this.$refs.dropdownlist.scrollTop = liH * this.currentItem;
		},
		loadMoreHandle() {
			this.loadingMore = true;
			this.$emit('load-more');
		}
	},
	watch: {
		options(newValue) {
			this.optionsLocal = Array.from(newValue);
		},
		searchQuery(newValue) {
			if (!this.moreToLoad) {
				if (newValue) {
					const valueToSearch = decodeURI(newValue.toLowerCase());

					this.optionsLocal = this.options.filter(option =>
						option.text?.toLowerCase().includes(valueToSearch)
					);
				} else {
					this.optionsLocal = [...this.options];
				}
			}
			this.loadingSearch = true;
			this.$emit('search', newValue);
		}
	}
};
</script>

<style lang="scss" scoped>
@import '@/scss/5-objects/_form.scss';

/*------------------------------------*\
  #DROPDOWN LIST with <ul> and <li>
\*------------------------------------*/

.dropdown__list-container {
	@include stylized-dropdown;
	width: max-content;
	min-width: 100%;
	z-index: 999;
}

.dropdown-list {
	padding-inline-start: 0;
	margin-block-start: 2px;
	margin-block-end: 0;
	font-size: $default-font-size - 2;
	max-height: 200px; // display scroll if there's more than 10 items
	overflow-y: auto;
	@include scrollbar('small');
	padding: $padding-sm 0;

	&__item {
		position: relative;

		&:active {
			::v-deep .checkbox__input {
				border: $form-input-border;
				background-color: $form-input-bkgd;
			}
		}

		&:focus::before {
			content: '';
			@include vertical-horizontal-center-with-transform;
			background-color: none;
			width: 100%;
			height: 100%;
			border: $form-input-border--focus;
		}

		&:checked {
			border: $form-input-border--checked;
		}

		&:focus-visible {
			outline: none;
		}

		cursor: pointer;
		padding: $padding-sm;

		&.is-active {
			background: $neutral-4;
			@include text-contrast($neutral-4, $white, $black);
		}

		&:hover,
		&.selected {
			background: lighten($neutral-4, 5%);
			@include text-contrast(lighten($neutral-4, 5%), $white, $black);
		}

		&.disabled {
			pointer-events: none;
			opacity: $disabled-opacity;
		}

		.list-item {
			display: inline-flex;
			width: 100%;

			&__link {
				word-break: break-all;
			}
		}
	}
	&__divider {
		width: 100%;
	}
}
.search {
	padding: $padding-sm $padding-sm 0 $padding-sm;
}
</style>
